import React, { useState } from "react";
import {
  Sparkles,
  ShieldCheck,
  Cpu,
  TrendingUp,
  MapPin,
  Phone,
  Mail,
  Clock,
  ArrowRight,
  Code2,
  Cloud,
  Lock,
  Headphones,
  BarChart3,
  Server,
  HeartPulse,
  Landmark,
  ShoppingCart,
  Factory,
  GraduationCap,
  Truck,
  CheckCircle2,
  ChevronRight,
  Building2,
  Boxes,
} from "lucide-react";

export default function AboutUs() {
  const [activeTab, setActiveTab] = useState("services");

  // Services dataset
  const services = [
    {
      name: "Software Development",
      icon: Code2,
      desc: "Custom high-performance web, mobile, and enterprise platforms tailored to your business goals.",
    },
    {
      name: "Cloud Solutions",
      icon: Cloud,
      desc: "Seamless cloud migration, multi-cloud strategy, and automated DevOps infrastructure.",
    },
    {
      name: "Cyber Security",
      icon: Lock,
      desc: "Proactive threat detection, vulnerability assessments, and zero-trust security architectures.",
    },
    {
      name: "IT Consulting",
      icon: Headphones,
      desc: "Strategic tech roadmaps, digital transformation planning, and legacy modernization.",
    },
    {
      name: "Managed IT Services",
      icon: Server,
      desc: "24/7 infrastructure monitoring, maintenance, and enterprise system reliability management.",
    },
    {
      name: "Data Analytics",
      icon: BarChart3,
      desc: "Turn raw big data into actionable strategic insights with business intelligence & AI reporting.",
    },
  ];

  // Industries dataset
  const industries = [
    {
      name: "Healthcare",
      icon: HeartPulse,
      desc: "HIPAA-compliant software, predictive patient diagnostics, and telemedicine platforms.",
    },
    {
      name: "Finance",
      icon: Landmark,
      desc: "Secure fintech apps, algorithmic risk assessment, and automated compliance engines.",
    },
    {
      name: "Retail",
      icon: ShoppingCart,
      desc: "Omnichannel e-commerce, AI supply chain forecast, and smart inventory management.",
    },
    {
      name: "Manufacturing",
      icon: Factory,
      desc: "IoT smart factory automation, predictive maintenance, and quality assurance systems.",
    },
    {
      name: "Education",
      icon: GraduationCap,
      desc: "Interactive LMS portals, virtual classrooms, and automated administrative workflows.",
    },
    {
      name: "Logistics",
      icon: Truck,
      desc: "Real-time fleet telematics, dynamic route optimization, and automated dispatch engines.",
    },
  ];

  return (
    <div className="bg-slate-50 text-slate-800 font-sans min-h-screen overflow-x-hidden selection:bg-[#f25c05] selection:text-white">
      {/* ========================================================================= */}
      {/* SECTION 1: HERO / WHO WE ARE (Clean Light White & Slate Design)           */}
      {/* ========================================================================= */}
      <section className="relative bg-white text-slate-900 py-20 sm:py-28 lg:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden border-b border-slate-200/80">
        {/* Subtle Background Pattern & Glows */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000008_1px,transparent_1px),linear-gradient(to_bottom,#00000008_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />
        <div className="absolute top-10 right-10 w-96 h-96 bg-orange-100/60 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute -bottom-10 left-10 w-96 h-96 bg-slate-100 rounded-full blur-[100px] pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Main Text Content */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-50 border border-orange-200/60 text-[#f25c05] text-xs font-extrabold uppercase tracking-widest shadow-sm">
                <Sparkles size={14} />
                About Inimai Solutions
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-950 leading-[1.12] tracking-tight">
                An <span className="text-[#f25c05]">AI-First</span> Technology Partner
              </h1>

              <p className="text-slate-600 text-base sm:text-lg md:text-xl leading-relaxed font-normal max-w-2xl">
                Inimai Solutions is an AI-first technology company delivering innovative, scalable, and secure solutions to help businesses innovate, automate, and grow.
              </p>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <a
                  href="/contact"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-[#f25c05] hover:bg-[#d84e00] active:scale-95 text-white font-bold py-3.5 px-8 rounded-xl transition-all duration-200 shadow-xl shadow-[#f25c05]/25 text-sm sm:text-base cursor-pointer"
                >
                  Get in Touch
                  <ArrowRight size={18} />
                </a>
                <a
                  href="#capabilities"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 active:scale-95 text-slate-700 font-bold py-3.5 px-7 rounded-xl transition-all duration-200 text-sm sm:text-base border border-slate-200/80 cursor-pointer"
                >
                  Explore Capabilities
                </a>
              </div>

              {/* Trust Badges */}
              <div className="pt-6 border-t border-slate-100 grid grid-cols-3 gap-4 text-xs sm:text-sm font-semibold text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-[#f25c05]" />
                  <span>Enterprise AI</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-[#f25c05]" />
                  <span>Cloud Native</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-[#f25c05]" />
                  <span>ISO Standards</span>
                </div>
              </div>
            </div>

            {/* Visual Value Pillar Cards */}
            <div className="lg:col-span-5 space-y-4">
              <div className="bg-white/80 backdrop-blur-md border border-slate-200/80 p-6 rounded-2xl shadow-xl shadow-slate-200/50 flex items-start gap-5 hover:border-orange-300 transition-all group">
                <div className="p-3.5 bg-orange-50 text-[#f25c05] rounded-xl group-hover:bg-[#f25c05] group-hover:text-white transition-colors duration-300 shrink-0">
                  <Cpu size={26} />
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-900 text-lg">Innovative</h3>
                  <p className="text-slate-600 text-xs sm:text-sm mt-1 leading-relaxed">
                    Building next-generation AI automation frameworks and custom software ecosystems.
                  </p>
                </div>
              </div>

              <div className="bg-white/80 backdrop-blur-md border border-slate-200/80 p-6 rounded-2xl shadow-xl shadow-slate-200/50 flex items-start gap-5 hover:border-orange-300 transition-all group">
                <div className="p-3.5 bg-orange-50 text-[#f25c05] rounded-xl group-hover:bg-[#f25c05] group-hover:text-white transition-colors duration-300 shrink-0">
                  <TrendingUp size={26} />
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-900 text-lg">Scalable</h3>
                  <p className="text-slate-600 text-xs sm:text-sm mt-1 leading-relaxed">
                    Cloud-native architectures engineered for high-frequency performance and rapid expansion.
                  </p>
                </div>
              </div>

              <div className="bg-white/80 backdrop-blur-md border border-slate-200/80 p-6 rounded-2xl shadow-xl shadow-slate-200/50 flex items-start gap-5 hover:border-orange-300 transition-all group">
                <div className="p-3.5 bg-orange-50 text-[#f25c05] rounded-xl group-hover:bg-[#f25c05] group-hover:text-white transition-colors duration-300 shrink-0">
                  <ShieldCheck size={26} />
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-900 text-lg">Secure</h3>
                  <p className="text-slate-600 text-xs sm:text-sm mt-1 leading-relaxed">
                    Enterprise cyber security protocols protecting critical data assets and infrastructure.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* SECTION 2: SERVICES & INDUSTRIES HUB (Dynamic Interactive Section)       */}
      {/* ========================================================================= */}
      <section id="capabilities" className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 space-y-3">
          <span className="text-xs font-extrabold uppercase tracking-widest text-[#f25c05] block">
            What We Do
          </span>
          <h2 className="text-3xl sm:text-4xl font-black text-slate-950 tracking-tight">
            Capabilities & Industry Reach
          </h2>
          <p className="text-slate-600 text-sm sm:text-base">
            Explore our end-to-end technical capabilities and domain expertise designed to transform modern enterprises.
          </p>

          {/* Interactive Toggle Switch */}
          <div className="pt-4 flex justify-center">
            <div className="inline-flex p-1.5 rounded-2xl bg-slate-200/80 border border-slate-300/60 text-sm font-bold">
              <button
                onClick={() => setActiveTab("services")}
                className={`px-6 py-2.5 rounded-xl transition-all duration-200 flex items-center gap-2 cursor-pointer ${
                  activeTab === "services"
                    ? "bg-[#f25c05] text-white shadow-md shadow-[#f25c05]/20"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Boxes size={16} />
                Core Services ({services.length})
              </button>

              <button
                onClick={() => setActiveTab("industries")}
                className={`px-6 py-2.5 rounded-xl transition-all duration-200 flex items-center gap-2 cursor-pointer ${
                  activeTab === "industries"
                    ? "bg-[#f25c05] text-white shadow-md shadow-[#f25c05]/20"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Building2 size={16} />
                Target Industries ({industries.length})
              </button>
            </div>
          </div>
        </div>

        {/* Dynamic Services Tab Content */}
        {activeTab === "services" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-fadeIn">
            {services.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={idx}
                  className="bg-white border border-slate-200/90 p-7 rounded-3xl shadow-lg shadow-slate-200/40 hover:shadow-xl hover:border-orange-300 transition-all duration-300 flex flex-col justify-between group"
                >
                  <div className="space-y-4">
                    <div className="p-3.5 bg-orange-50 text-[#f25c05] w-fit rounded-2xl group-hover:bg-[#f25c05] group-hover:text-white transition-colors duration-300">
                      <Icon size={26} />
                    </div>
                    <h3 className="font-extrabold text-slate-900 text-lg sm:text-xl">
                      {item.name}
                    </h3>
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                      {item.desc}
                    </p>
                  </div>

                  <div className="pt-6 mt-6 border-t border-slate-100 flex items-center text-[#f25c05] font-bold text-xs uppercase tracking-wider gap-1 group-hover:gap-2 transition-all">
                    <span>Learn More</span>
                    <ChevronRight size={14} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Dynamic Industries Tab Content */}
        {activeTab === "industries" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-fadeIn">
            {industries.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={idx}
                  className="bg-white border border-slate-200/90 p-7 rounded-3xl shadow-lg shadow-slate-200/40 hover:shadow-xl hover:border-orange-300 transition-all duration-300 flex flex-col justify-between group"
                >
                  <div className="space-y-4">
                    <div className="p-3.5 bg-orange-50 text-[#f25c05] w-fit rounded-2xl group-hover:bg-[#f25c05] group-hover:text-white transition-colors duration-300">
                      <Icon size={26} />
                    </div>
                    <h3 className="font-extrabold text-slate-900 text-lg sm:text-xl">
                      {item.name}
                    </h3>
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                      {item.desc}
                    </p>
                  </div>

                  <div className="pt-6 mt-6 border-t border-slate-100 flex items-center text-[#f25c05] font-bold text-xs uppercase tracking-wider gap-1 group-hover:gap-2 transition-all">
                    <span>Industry Solutions</span>
                    <ChevronRight size={14} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ========================================================================= */}
      {/* SECTION 3: HEADQUARTERS & CONTACT BANNER (High-Contrast Dark Hub)         */}
      {/* ========================================================================= */}
      <section className="bg-slate-950 text-white py-16 sm:py-24 px-4 sm:px-6 lg:px-8 border-t border-slate-900 relative overflow-hidden">
        {/* Glow Effects */}
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[500px] h-[250px] bg-[#f25c05]/15 rounded-full blur-[130px] pointer-events-none" />

        <div className="max-w-7xl mx-auto bg-slate-900/90 border border-slate-800 p-8 sm:p-12 lg:p-14 rounded-3xl shadow-2xl relative z-10 backdrop-blur-md">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Left Header */}
            <div className="lg:col-span-5 space-y-4 text-left">
              <span className="text-xs font-extrabold uppercase tracking-widest text-[#f25c05]">
                Global Headquarters
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight leading-tight">
                Connect With Our Engineering Team
              </h2>
              <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
                Have a project in mind or need custom technical guidance? Visit our Chennai office or drop us a line today.
              </p>

              <div className="pt-4">
                <a
                  href="mailto:info@inim.ai"
                  className="inline-flex items-center gap-2 bg-[#f25c05] hover:bg-[#d84e00] active:scale-95 text-white font-bold py-3.5 px-7 rounded-xl transition duration-200 shadow-xl shadow-[#f25c05]/20 text-sm sm:text-base"
                >
                  Send Direct Inquiry
                  <ChevronRight size={18} />
                </a>
              </div>
            </div>

            {/* Right Contact Details Grid */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex items-start gap-4 p-5 bg-slate-950/70 rounded-2xl border border-slate-800/80 hover:border-slate-700 transition">
                <div className="p-3 bg-orange-500/10 text-[#f25c05] rounded-xl shrink-0 mt-0.5">
                  <MapPin size={22} />
                </div>
                <div className="space-y-1">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Address</h4>
                  <p className="text-xs sm:text-sm text-slate-200 font-medium leading-relaxed">
                    No 33 Old Mahabaliburam Road,
Seevaram, Perungudi,
Chennai - 600096, India
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-slate-950/70 rounded-2xl border border-slate-800/80 hover:border-slate-700 transition">
                <div className="p-3 bg-orange-500/10 text-[#f25c05] rounded-xl shrink-0 mt-0.5">
                  <Phone size={22} />
                </div>
                <div className="space-y-1">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Call Us</h4>
                  <p className="text-xs sm:text-sm text-slate-200 font-medium">
                    +91 95666 63558
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-slate-950/70 rounded-2xl border border-slate-800/80 hover:border-slate-700 transition">
                <div className="p-3 bg-orange-500/10 text-[#f25c05] rounded-xl shrink-0 mt-0.5">
                  <Mail size={22} />
                </div>
                <div className="space-y-1">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Email</h4>
                  <p className="text-xs sm:text-sm text-slate-200 font-medium">
                    contactus@inim-ai.com
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-slate-950/70 rounded-2xl border border-slate-800/80 hover:border-slate-700 transition">
                <div className="p-3 bg-orange-500/10 text-[#f25c05] rounded-xl shrink-0 mt-0.5">
                  <Clock size={22} />
                </div>
                <div className="space-y-1">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Working Hours</h4>
                  <p className="text-xs sm:text-sm text-slate-200 font-medium">
                    Mon – Sat: 9:00 AM – 6:00 PM
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}